import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import { useContent } from "../../hooks/useContent";
import FormButtons from "../../Components/GeneralComponents/FormButtons";
import { useNavigate, useParams } from "react-router-dom";
import { add_company_api, get_company_detail_api } from "../../DAL/Company";
import { useAuth } from "../../hooks/useAuth";

const CreateOrUpdateCompany = () => {
  const { setPageHeading } = useContent();
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const { company_id } = useParams();

  const [inputs, setInputs] = useState({
    partner_id: userInfo.id,
    company_name: "",
    company_web_site: "",
    company_logo_url: "",
    purchased_product_license_code: "",
    number_of_licenses_assigned: "",
    admin_username: userInfo.email,
    admin_password: "",
    primary_contact_first_name: userInfo.first_name,
    primary_contact_middle_name: "",
    primary_contact_last_name: userInfo.last_name,
    primary_contact_email_address: userInfo.email,
    primary_contact_phone_number: userInfo.phone_mobile,
    ship_to_addres: "",
    ship_to_city: "",
    ship_to_state: "",
    ship_to_zipcode: "",
    ship_to_country: "",
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().format("YYYY-MM-DD"),
    status: "ACTIVE",
    component_sold: "",
    notes: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const getCompanyDetail = async () => {
    const result = await get_company_detail_api(company_id);
    if (result.code === 200) {
      setInputs((old) => ({ ...old, ...result.company }));
    } else {
      toast.error(`${result.message}`);
    }
    setIsLoadingPage(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, value) => {
    setInputs((prev) => ({
      ...prev,
      [name]: value ? value.format("YYYY-MM-DD") : "",
    }));
  };

  const showToast = (message) => toast.error(`${message}`);

  const validate = () => {
    if (!inputs.company_name.trim())
      return showToast("Company name is required");
    if (!inputs.admin_username.trim())
      return showToast("Admin email is required");
    if (!inputs.admin_password.trim()) return showToast("Password is required");
    if (!inputs.number_of_licenses_assigned.trim())
      return showToast("Number of licenses is required");
    if (!inputs.primary_contact_first_name.trim())
      return showToast("First name is required");
    if (!inputs.primary_contact_last_name.trim())
      return showToast("Last name is required");
    if (!inputs.primary_contact_phone_number.trim())
      return showToast("Phone number is required");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    const result = await add_company_api(inputs);
    if (result.code === 200) {
      toast.success(`${result.message}`);
      navigate(`/home`);
    } else {
      toast.error(`${result.message}`);
    }
    setIsLoading(false);
  };

  const handleCancel = () => navigate("/manage-companies");

  useEffect(() => {
    setPageHeading(`${company_id ? "Update" : "Create"}  Company`);
    if (company_id) {
      getCompanyDetail();
    } else {
      setIsLoadingPage(false);
    }
  }, [company_id]);

  return (
    <div className="login-page">
      <div className="d-flex justify-content-center">
        <div className="container mx-auto">
          <div className="form-wrapper">
            <form onSubmit={handleSubmit}>
              {/* <div className="d-flex justify-content-end mb-4">
                <FormButtons {...{ isLoading, handleCancel }} />
              </div> */}
              <div className="form-group mb-3">
                <label>Partner ID</label>
                <div className="input-wrapper">
                  <Icon icon="mdi:account-outline" className="input-icon" />
                  <input
                    type="text"
                    name="partner_id"
                    value={inputs.partner_id}
                    placeholder="Partner ID"
                    className="form-control"
                    readOnly
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>
                      Company Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon
                        icon="mdi:office-building-outline"
                        className="input-icon"
                      />
                      <input
                        type="text"
                        name="company_name"
                        value={inputs.company_name}
                        onChange={handleChange}
                        placeholder="Company Name"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>
                      Number of Licenses <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <span className="input-icon">#</span>
                      <input
                        type="text"
                        name="number_of_licenses_assigned"
                        value={inputs.number_of_licenses_assigned}
                        onChange={handleChange}
                        placeholder="Number of Licenses"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>License Start Date</label>
                    <div className="input-wrapper">
                      <Icon
                        icon="mdi:calendar"
                        className="input-icon datepicker-icon"
                      />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          inputFormat="DD/MM/YYYY"
                          className="form-control mui-date-picker"
                          value={
                            inputs.start_date ? dayjs(inputs.start_date) : null
                          }
                          onChange={(v) => handleDateChange("start_date", v)}
                          maxDate={dayjs()}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>License End Date</label>
                    <div className="input-wrapper">
                      <Icon
                        icon="mdi:calendar"
                        className="input-icon datepicker-icon"
                      />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          inputFormat="DD/MM/YYYY"
                          className="form-control mui-date-picker"
                          value={
                            inputs.end_date ? dayjs(inputs.end_date) : null
                          }
                          onChange={(v) => handleDateChange("end_date", v)}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>Company Logo URL</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:image-outline" className="input-icon" />
                      <input
                        type="text"
                        name="company_logo_url"
                        value={inputs.company_logo_url}
                        onChange={handleChange}
                        placeholder="https://example.com/logo.png"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>Company Website</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:web" className="input-icon" />
                      <input
                        type="text"
                        name="company_web_site"
                        value={inputs.company_web_site}
                        onChange={handleChange}
                        placeholder="https://www.company.com"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <h4 className="my-3 middle-heading">Primary Contact</h4>
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>
                      Admin Email <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:email-outline" className="input-icon" />
                      <input
                        type="email"
                        name="admin_username"
                        value={inputs.admin_username}
                        onChange={handleChange}
                        placeholder="Admin Email"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>
                      Password <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:lock-outline" className="input-icon" />
                      <input
                        type="password"
                        name="admin_password"
                        value={inputs.admin_password}
                        onChange={handleChange}
                        placeholder="Password"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>
                      First Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:account-outline" className="input-icon" />
                      <input
                        type="text"
                        name="primary_contact_first_name"
                        value={inputs.primary_contact_first_name}
                        onChange={handleChange}
                        placeholder="First Name"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>
                      Last Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:account-outline" className="input-icon" />
                      <input
                        type="text"
                        name="primary_contact_last_name"
                        value={inputs.primary_contact_last_name}
                        onChange={handleChange}
                        placeholder="Last Name"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group mb-3">
                <label>
                  Phone Number <span className="text-danger">*</span>
                </label>
                <div className="input-wrapper">
                  <Icon icon="mdi:phone-outline" className="input-icon" />
                  <input
                    type="text"
                    name="primary_contact_phone_number"
                    value={inputs.primary_contact_phone_number}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    className="form-control"
                    required
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end mb-4">
                <FormButtons
                  {...{
                    isLoading,
                    handleCancel,
                    isRelative: false,
                  }}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrUpdateCompany;
